import React, { useState, useEffect } from "react";
import { FaEdit, FaTimes } from "react-icons/fa";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../../../firebase";
import "./CustomerDrawer.css";

// Helper to format date
const formatDate = (dateString) => {
  try {
    if (dateString && dateString.length === 10) {
      return new Date(dateString + "T00:00:00").toLocaleDateString();
    }
    return new Date(dateString).toLocaleDateString();
  } catch (e) {
    return dateString;
  }
};

const CustomerDrawer = ({
  customer,
  onClose,
  onUpdateBalance,
  onBalanceUpdated,
}) => {
  const [showAmountModal, setShowAmountModal] = useState(false);
  const [showGoldModal, setShowGoldModal] = useState(false);
  const [amountValue, setAmountValue] = useState("");
  const [goldValue, setGoldValue] = useState("");
  const [transactionDate, setTransactionDate] = useState(
    new Date().toISOString().substring(0, 10)
  );

  const [selectedOrderId, setSelectedOrderId] = useState("");

  const [ongoingOrders, setOngoingOrders] = useState([]);
  // State to hold only orders that still need payments (for dropdowns)
  const [ordersForDropdown, setOrdersForDropdown] = useState([]);
  const [isLoadingOrders, setIsLoadingOrders] = useState(true);

  // --- NEW: Calculate Total Payments (Revenue) from amountHistory ---
  const totalAmountPaid =
    customer.amountHistory?.reduce((sum, entry) => {
      // Only sum payments (Credit transactions)
      if (entry.type === "Credit") {
        return sum + (entry.amount || 0);
      }
      return sum;
    }, 0) || 0;
  // -----------------------------------------------------------------

  // 🔑 UPDATED: useEffect to fetch all relevant orders AND filter for the dropdown
  useEffect(() => {
    const fetchOrders = async () => {
      if (!customer?.id) return;
      setIsLoadingOrders(true);
      try {
        // Query orders that are either "Ongoing" or "Completed"
        const ordersQuery = query(
          collection(db, "orders"),
          where("customerId", "==", customer.id),
          where("status", "in", ["Ongoing", "Completed"])
        );

        const snapshot = await getDocs(ordersQuery);
        const fetchedOrders = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          // Helper property for display
          shortId: doc.id.substring(0, 6),
        }));

        // Filter for the specific Ongoing Orders section
        setOngoingOrders(
          fetchedOrders.filter((order) => order.status === "Ongoing")
        );

        // 🔑 KEY LOGIC: Filter orders for dropdown to show only those with an OUTSTANDING BALANCE.
        // Orders are shown ONLY IF totalRemainingAmount OR totalRemainingGold is outstanding (> 0.001).
        const filteredOrdersForDropdown = fetchedOrders.filter((order) => {
          // Use commonDetails or root level fields depending on your Firebase structure
          const remainingAmount = order.totalRemainingAmount || 0;
          const remainingGold = order.totalRemainingGold || 0;

          // Check if either remaining balance is positive
          return (
            Math.abs(remainingAmount) > 0.001 || Math.abs(remainingGold) > 0.001
          );
        });

        // Set the filtered orders for the dropdowns
        setOrdersForDropdown(filteredOrdersForDropdown);

        // Automatically select the first relevant order if available
        if (filteredOrdersForDropdown.length > 0) {
          setSelectedOrderId(filteredOrdersForDropdown[0].id);
        } else {
          setSelectedOrderId("");
        }
      } catch (error) {
        console.error("Error fetching relevant orders:", error);
      } finally {
        setIsLoadingOrders(false);
      }
    };

    fetchOrders();
  }, [customer.id]);

  const handleUpdateAmount = (e) => {
    e.preventDefault();
    if (amountValue && transactionDate) {
      onUpdateBalance(
        customer.id,
        "amount",
        amountValue,
        transactionDate,
        selectedOrderId
      );

      // Trigger re-fetch of customers on the parent component to update balances
      if (onBalanceUpdated) onBalanceUpdated();

      setShowAmountModal(false);
      setAmountValue("");
      setTransactionDate(new Date().toISOString().substring(0, 10));
    }
  };

  const handleUpdateGold = (e) => {
    e.preventDefault();
    if (goldValue && transactionDate) {
      onUpdateBalance(
        customer.id,
        "gold",
        goldValue,
        transactionDate,
        selectedOrderId
      );

      // Trigger re-fetch of customers on the parent component to update balances
      if (onBalanceUpdated) onBalanceUpdated();

      setShowGoldModal(false);
      setGoldValue("");
      setTransactionDate(new Date().toISOString().substring(0, 10));
    }
  };

  // Helper to format order product details (unchanged)
  const getProductSummary = (products) => {
    if (!products) return "No products specified";
    const productKeys = Object.keys(products);
    if (productKeys.length === 0) return "No products specified";

    const firstProductDetails = products[productKeys[0]];
    const totalCount = productKeys.length;

    return `${totalCount} item${totalCount > 1 ? "s" : ""} (e.g., ${
      firstProductDetails.weight
    } Gold)`;
  };

  // 🛠️ Helper to get the correct description for the dropdown (includes status and assumed remaining balances)
  const getOrderDropdownLabel = (order) => {
    const remAmount = (order.totalRemainingAmount || 0).toFixed(0);
    const remGold = (order.totalRemainingGold || 0).toFixed(2);

    // Show remaining balance in the label for clarity
    return `ID: ${order.shortId} (${order.status}) | Pending: ₹${remAmount} / ${remGold} gm`;
  };

  return (
    <div className="drawer-overlay" onClick={onClose}>
      <div className="drawer-content" onClick={(e) => e.stopPropagation()}>
        <button className="drawer-close-btn" onClick={onClose}>
          <FaTimes />
        </button>

        <div className="customer-info-header">
          <h2>{customer.name}</h2>

          {/* --- NEW: Contact Details and Total Payments Summary --- */}
          <div className="customer-details-summary">
            <p>
              <strong>Email:</strong> {customer.email || "N/A"}
            </p>
            <p>
              <strong>Mobile:</strong> {customer.mobile || "N/A"}
            </p>
            <p>
              <strong>WhatsApp:</strong> {customer.whatsapp || "N/A"}
            </p>
            <p className="total-revenue">
              <strong>Total Payments (Revenue):</strong>
              <span className="positive"> ₹{totalAmountPaid.toFixed(0)}</span>
            </p>
          </div>
          {/* ---------------------------------------------------- */}
        </div>

        {/* Balance Amount Section */}
        <div className="balance-section">
          <div className="balance-header">
            <h3>Balance Amount</h3>
            <button
              className="edit-balance-btn"
              onClick={() => {
                setShowAmountModal(true);
                if (ordersForDropdown.length > 0) {
                  setSelectedOrderId(ordersForDropdown[0].id);
                } else {
                  setSelectedOrderId("");
                }
              }}
            >
              <FaEdit /> Edit
            </button>
          </div>
          <p className="balance-value">₹{customer.balanceAmount || 0}</p>
          <div className="history-table">
            <h4>History</h4>
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Type</th>
                  <th>Order ID</th>
                </tr>
              </thead>
              <tbody>
                {customer.amountHistory && customer.amountHistory.length > 0 ? (
                  customer.amountHistory
                    .sort((a, b) => new Date(b.date) - new Date(a.date))
                    .map((entry, index) => (
                      <tr key={index}>
                        <td>{formatDate(entry.date)}</td>
                        <td
                          className={
                            entry.type === "Credit" ? "positive" : "negative"
                          }
                        >
                          ₹{entry.amount}
                        </td>
                        <td>{entry.type}</td>
                        <td>{entry.orderId ? `#${entry.orderId}` : "N/A"}</td>
                      </tr>
                    ))
                ) : (
                  <tr>
                    <td colSpan="4">No transactions yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* --- */}

        {/* Balance Gold Section */}
        <div className="balance-section">
          <div className="balance-header">
            <h3>Balance Gold</h3>
            <button
              className="edit-balance-btn"
              onClick={() => {
                setShowGoldModal(true);
                if (ordersForDropdown.length > 0) {
                  setSelectedOrderId(ordersForDropdown[0].id);
                } else {
                  setSelectedOrderId("");
                }
              }}
            >
              <FaEdit /> Edit
            </button>
          </div>
          <p className="balance-value">{customer.balanceGold || 0} gm</p>
          <div className="history-table">
            <h4>History</h4>
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Gold</th>
                  <th>Type</th>
                  <th>Order ID</th>
                </tr>
              </thead>
              <tbody>
                {customer.goldHistory && customer.goldHistory.length > 0 ? (
                  customer.goldHistory
                    .sort((a, b) => new Date(b.date) - new Date(a.date))
                    .map((entry, index) => (
                      <tr key={index}>
                        <td>{formatDate(entry.date)}</td>
                        <td
                          className={
                            entry.type === "Credit" ? "positive" : "negative"
                          }
                        >
                          {entry.gold} gm
                        </td>
                        <td>{entry.type}</td>
                        <td>{entry.orderId ? `#${entry.orderId}` : "N/A"}</td>
                      </tr>
                    ))
                ) : (
                  <tr>
                    <td colSpan="4">No gold transactions yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* --- */}

        {/* Orders Section: Displaying ONLY Ongoing Orders */}
        <div className="orders-section">
          <h3>Ongoing Orders ({ongoingOrders.length || 0})</h3>
          {isLoadingOrders ? (
            <p>Loading orders...</p>
          ) : ongoingOrders.length > 0 ? (
            <div className="orders-list">
              {ongoingOrders.map((order) => (
                <div key={order.id} className="order-item">
                  <p>
                    <strong>Order ID:</strong> #{order.shortId}
                  </p>
                  <p>
                    <strong>Items:</strong> {getProductSummary(order.products)}
                  </p>
                  <p>
                    <strong>Advance on Order:</strong> ₹
                    {order.commonDetails?.advanceAmount || 0} /{" "}
                    {order.commonDetails?.advanceGold || 0} gm
                  </p>
                  <p>
                    <strong>Delivery:</strong>{" "}
                    {formatDate(
                      order.products[Object.keys(order.products)[0]]
                        ?.deliveryDate || ""
                    )}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-orders">
              No ongoing orders found for this customer.
            </p>
          )}
        </div>

        {/* --- */}

        {/* Amount Edit Modal */}
        {showAmountModal && (
          <div className="edit-modal">
            <div className="modal-content">
              <h4>Update Amount</h4>
              <form onSubmit={handleUpdateAmount}>
                {/* ORDER ID DROP DOWN - USES filtered orders */}
                <label>
                  Link to Order (Only for orders with pending balance)
                  <select
                    value={selectedOrderId}
                    onChange={(e) => setSelectedOrderId(e.target.value)}
                    disabled={ordersForDropdown.length === 0}
                  >
                    <option value="">-- General Transaction (N/A) --</option>
                    {ordersForDropdown.map((order) => (
                      <option key={order.id} value={order.id}>
                        {getOrderDropdownLabel(order)}
                      </option>
                    ))}
                  </select>
                </label>
                {/* END ORDER ID DROP DOWN */}

                <input
                  type="date"
                  value={transactionDate}
                  onChange={(e) => setTransactionDate(e.target.value)}
                  required
                />
                <input
                  type="number"
                  value={amountValue}
                  onChange={(e) => setAmountValue(e.target.value)}
                  placeholder="Enter amount (e.g., 500 for paid, -200 for debited)"
                  required
                />
                <div className="modal-actions">
                  <button type="submit">Save</button>
                  <button
                    type="button"
                    onClick={() => setShowAmountModal(false)}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Gold Edit Modal */}
        {showGoldModal && (
          <div className="edit-modal">
            <div className="modal-content">
              <h4>Update Gold</h4>
              <form onSubmit={handleUpdateGold}>
                {/* ORDER ID DROP DOWN - USES filtered orders */}
                <label>
                  Link to Order (Only for orders with pending balance)
                  <select
                    value={selectedOrderId}
                    onChange={(e) => setSelectedOrderId(e.target.value)}
                    disabled={ordersForDropdown.length === 0}
                  >
                    <option value="">-- General Transaction (N/A) --</option>
                    {ordersForDropdown.map((order) => (
                      <option key={order.id} value={order.id}>
                        {getOrderDropdownLabel(order)}
                      </option>
                    ))}
                  </select>
                </label>
                {/* END ORDER ID DROP DOWN */}

                <input
                  type="date"
                  value={transactionDate}
                  onChange={(e) => setTransactionDate(e.target.value)}
                  required
                />
                <input
                  type="number"
                  step="0.01"
                  value={goldValue}
                  onChange={(e) => setGoldValue(e.target.value)}
                  placeholder="Enter gold in gm (e.g., 5.00 for received, -2.50 for debited)"
                  required
                />
                <div className="modal-actions">
                  <button type="submit">Save</button>
                  <button type="button" onClick={() => setShowGoldModal(false)}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerDrawer;
